<template>
  <div class="card-body">
    <!-- Success alert (auto-closes) -->
    <div v-if="showSuccess" class="alert alert-success mt-4" role="alert">
      {{ $t("account-page-facility-my-page-e-ticket.success_alert") }}
    </div>
    <h4 class="card-title">
      {{ $t("account-page-facility-my-page-icon-colors.card_title") }}
    </h4>
    <p>
      {{ $t("account-page-facility-my-page-icon-colors.index.description") }}
    </p>
    <span class="badge badge-outline-info">{{ $t("account-page-facility-my-page-icon-colors.index.sample") }}</span>
    <hr />

    <!-- Current setting preview -->
    <div class="current-setting">
      <span class="current-label">{{ $t("account-page-facility-my-page-icon-colors.index.current_setting") }}:</span>
      <div class="icon-preview" :style="{ backgroundColor: hex('bg') }">
        <div
          class="sample-icon"
          :style="{ backgroundColor: hex('icon') }"
        ></div>
      </div>
    </div>

    <!-- Always-visible pickers -->
    <div class="pickers">
      <template v-for="(p, i) in pickers">
        <div v-if="i === 1" :key="p.key + '-divider'" class="divider"></div>

        <div :key="p.key" class="picker-col">
          <h5 class="picker-title">{{ $t(`account-page-facility-my-page-icon-colors.index.${p.title}`) }}</h5>

          <div class="cp">
            <div class="cp-top">
              <div class="cp-swatch" :style="{ background: hex(p.key) }"></div>

              <!-- Saturation / brightness panel -->
              <div
                class="cp-sat"
                :style="{
                  background: 'hsl(' + colors[p.key].h + ', 100%, 50%)',
                }"
                @pointerdown="onSatDown($event, p.key)"
                @pointermove="onSatMove($event, p.key)"
              >
                <div class="cp-white"></div>
                <div class="cp-black"></div>
                <div
                  class="cp-thumb"
                  :style="{
                    left: colors[p.key].s + '%',
                    top: 100 - colors[p.key].v + '%',
                  }"
                ></div>
              </div>
            </div>

            <!-- Hue slider -->
            <div
              class="cp-hue"
              @pointerdown="onHueDown($event, p.key)"
              @pointermove="onHueMove($event, p.key)"
            >
              <div
                class="cp-hue-thumb"
                :style="{ left: (colors[p.key].h / 360) * 100 + '%' }"
              ></div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Actions -->
    <div class="row mt-4">
      <div class="col-lg-12" align="center">
        <button
          @click.prevent="
            $router.push({
              name: 'checkin-facility-dashboard-my-page',
            })
          "
          class="btn btn-danger ml-3"
        >
          {{ $t("account-page-facility-my-page-message.back") }}
        </button>
        <!-- @click="$bvModal.show('modal-confirmation')" -->
        <button type="submit" class="btn btn-primary ml-3" @click="onConfirm">
          {{ $t("account-page-facility-my-page-message.confirm") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

function hsvToRgb(h, s, v) {
  s /= 100;
  v /= 100;
  const f = (n) => {
    const k = (n + h / 60) % 6;
    return v - v * s * Math.max(0, Math.min(k, 4 - k, 1));
  };
  return [f(5), f(3), f(1)].map((x) => Math.round(x * 255));
}

function hexToHsv(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const d = max - Math.min(r, g, b);
  let h = 0;
  if (d) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h = (h * 60 + 360) % 360;
  }
  return { h, s: max ? (d / max) * 100 : 0, v: max * 100 };
}

const toHex = (rgb) =>
  "#" + rgb.map((x) => x.toString(16).padStart(2, "0")).join("");

export default {
  name: "IconColors",
  data() {
    return {
      pickers: [
        { key: "icon", title: "icon_color" },
        { key: "bg", title: "background_color" },
      ],
      // h: 0-360, s: 0-100, v: 0-100
      colors: {
        icon: { h: 41, s: 75, v: 96 }, // default icon color (orange)
        bg: { h: 0, s: 0, v: 100 }, // default background color (white)
      },
      exists: false, // saved scheme found → PUT, otherwise POST
      showSuccess: false,
      alertTimer: null,
    };
  },
  computed: {
    facilityId() {
      return window._app_facility && window._app_facility.id;
    },
  },
  created() {
    this.fetchApi();
  },
  beforeDestroy() {
    clearTimeout(this.alertTimer);
  },
  methods: {
    async fetchApi() {
      try {
        console.log(this.facilityId);

        const r = await this.$lionheart.get(
          `/v2/facility-settings/color-scheme`,
          {
            params: { facility_id: this.facilityId },
          }
        );

        // nothing saved yet → keep the default colors
        if (!r.data.data || !r.data.data.value) return;

        const value = r.data.data.value;
        const icon = hexToHsv(value["icon_color"]);
        const bg = hexToHsv(value["background_color"]);

        if (icon) this.colors.icon = icon;
        if (bg) this.colors.bg = bg;
        this.exists = true; // add this
      } catch (e) {
        console.error(e);
      }
    },

    async onConfirm() {
      this.showSuccess = false;
      clearTimeout(this.alertTimer);

      const data = {
        facility_id: this.facilityId,
        value: {
          "background_color": this.hex("bg"),
          "icon_color": this.hex("icon"),
        },
      };

      try {
        if (this.exists)
          await this.$lionheart.put(`/v2/facility-settings/color-scheme`, data);
        else
          await this.$lionheart.post(
            `/v2/facility-settings/color-scheme`,
            data
          );

        this.exists = true;
        this.showSuccess = true;
        this.alertTimer = setTimeout(() => {
          this.showSuccess = false;
        }, 3000);
      } catch (e) {
        console.error(e);
      }
    },

    hex(key) {
      const c = this.colors[key];
      return toHex(hsvToRgb(c.h, c.s, c.v));
    },

    // Saturation / brightness panel
    onSatDown(e, key) {
      e.currentTarget.setPointerCapture(e.pointerId);
      this.updateSat(e, key);
    },
    onSatMove(e, key) {
      if (e.buttons === 1) this.updateSat(e, key);
    },
    updateSat(e, key) {
      const r = e.currentTarget.getBoundingClientRect();
      this.colors[key].s = clamp(
        ((e.clientX - r.left) / r.width) * 100,
        0,
        100
      );
      this.colors[key].v =
        100 - clamp(((e.clientY - r.top) / r.height) * 100, 0, 100);
    },

    // Hue slider
    onHueDown(e, key) {
      e.currentTarget.setPointerCapture(e.pointerId);
      this.updateHue(e, key);
    },
    onHueMove(e, key) {
      if (e.buttons === 1) this.updateHue(e, key);
    },
    updateHue(e, key) {
      const r = e.currentTarget.getBoundingClientRect();
      this.colors[key].h = clamp(
        ((e.clientX - r.left) / r.width) * 360,
        0,
        360
      );
    },
  },
};
</script>

<style scoped>
.current-setting {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 24px;
}
.current-label {
  color: #6c757d;
}
.icon-preview {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}
.sample-icon {
  width: 45px;
  height: 45px;
  -webkit-mask: url("../../../../assets/img/my-page/sample-icon.svg") no-repeat
    center / contain;
  mask: url("../../../../assets/img/my-page/sample-icon.svg") no-repeat center /
    contain;
}

.pickers {
  display: flex;
  justify-content: center;
  align-items: stretch;
}
.picker-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 0 24px;
}
.picker-title {
  font-weight: 600;
  color: #6c757d;
}
.divider {
  width: 1px;
  background: #6c757d;
}

/* ---- color picker ---- */
.cp {
  width: 100%;
  max-width: 380px;
  padding: 12px;
  background: #fff;
  border: 1px solid #e3e3e3;
  border-radius: 6px;
}
.cp-top {
  display: flex;
  height: 150px;
}
.cp-swatch {
  width: 33%;
}
.cp-sat {
  position: relative;
  flex: 1;
  cursor: crosshair;
  touch-action: none;
}
.cp-white,
.cp-black {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
}
.cp-white {
  background: linear-gradient(to right, #fff, rgba(255, 255, 255, 0));
}
.cp-black {
  background: linear-gradient(to top, #000, rgba(0, 0, 0, 0));
}
.cp-thumb {
  position: absolute;
  width: 14px;
  height: 14px;
  margin: -7px 0 0 -7px;
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.6);
  pointer-events: none;
}
.cp-hue {
  position: relative;
  height: 10px;
  margin: 16px 6px;
  border-radius: 5px;
  cursor: pointer;
  touch-action: none;
  background: linear-gradient(
    to right,
    #f00 0%,
    #ff0 17%,
    #0f0 33%,
    #0ff 50%,
    #00f 67%,
    #f0f 83%,
    #f00 100%
  );
}
.cp-hue-thumb {
  position: absolute;
  top: 50%;
  width: 16px;
  height: 16px;
  margin: -8px 0 0 -8px;
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.6);
  pointer-events: none;
}
</style>